import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRestaurant } from '../../contexts/RestaurantContext';
import {
  LayoutDashboard,
  Calendar,
  Plus,
  Settings,
  Users,
  BarChart3,
  LogOut,
  X,
  Store,
  Clock,
  Ban
} from 'lucide-react';

interface SidebarProps {
  onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const { user, logout } = useAuth();
  const { restaurant } = useRestaurant();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
      current: false,
    },
    {
      name: 'Reservas de Hoje',
      href: '/reservations/today',
      icon: Clock,
      current: false,
    },
    {
      name: 'Todas as Reservas',
      href: '/reservations',
      icon: Calendar,
      current: false,
    },
    {
      name: 'Nova Reserva',
      href: '/reservations/new',
      icon: Plus,
      current: false,
    },
    {
      name: 'Estatísticas',
      href: '/stats',
      icon: BarChart3,
      current: false,
    },
  ];

  // Add admin/manager specific items
  if (user?.tipo === 'admin_global' || user?.tipo === 'manager_restaurante') {
    navigationItems.push(
      {
        name: 'Disponibilidade',
        href: '/availability-closures',
        icon: Ban,
        current: false,
      },
      {
        name: 'Configurações',
        href: '/settings',
        icon: Settings,
        current: false,
      }
    );
  }

  if (user?.tipo === 'admin_global') {
    navigationItems.push(
      {
        name: 'Utilizadores',
        href: '/users',
        icon: Users,
        current: false,
      }
    );
  }

  return (
    <div className="flex h-full flex-col bg-white shadow-lg">
      {/* Header */}
      <div className="flex h-20 flex-shrink-0 items-center justify-between px-4 border-b border-secondary-200">
        <div className="flex items-center space-x-2">
         <img src="logo512.png" alt="Logo" className="h-10 w-10"/>
          <div className="flex flex-col">
            <span className="text-lg font-semibold text-secondary-900">
              {restaurant?.nome || 'Restaurante'}
            </span>
            <span className="text-xs text-secondary-500">
              {user?.tipo === 'admin_global' ? 'Administrador' : 
               user?.tipo === 'manager_restaurante' ? 'Gestor' : 'Staff'}
            </span>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="md:hidden text-secondary-400 hover:text-secondary-600"
          >
            <X className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-2 py-4">
        {navigationItems.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            end={item.href === '/reservations'}
            onClick={onClose}
            className={({ isActive }) =>
              `group flex items-center rounded-md px-2 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
              }`
            }
          >
            <item.icon
              className="mr-3 h-5 w-5 flex-shrink-0"
              aria-hidden="true"
            />
            {item.name}
          </NavLink>
        ))}
      </nav>

      {/* User Info & Logout */}
      <div className="flex-shrink-0 border-t border-secondary-200 p-4">
        <div className="mb-3">
          <p className="text-sm font-medium text-secondary-900">{user?.nome}</p>
          <p className="text-xs text-secondary-500">{user?.email}</p>
        </div>
        <button
          onClick={handleLogout}
          className="flex w-full items-center rounded-md px-2 py-2 text-sm font-medium text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900 transition-colors"
        >
          <LogOut className="mr-3 h-5 w-5" />
          Sair
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
